// Logic to determine when a destination address is required for cross-chain swaps

import { isCrossChainSwap, getTokenPrimaryChain, getTokenChains, type ChainId as NumericChainId } from './tokenRegistry';
import { getChainMetadata, type ChainId as StringChainId, getNumericChainId } from './chainRegistry';
import { getAddressFormatForChain, getStringChainIdFromNumeric } from './addressValidation';
import { isAtomicSwapCompatible } from './atomicSwapUtils';

/**
 * Determine if a destination address is required for a swap
 * @param fromToken - Source token symbol
 * @param toToken - Destination token symbol
 * @param walletType - Type of wallet ('web3', 'ravencoin', etc.)
 * @param fromChainId - Optional source chain ID (numeric)
 * @param toChainId - Optional destination chain ID (numeric)
 * @returns true if destination address is required
 */
export function requiresDestinationAddress(
  fromToken: string,
  toToken: string,
  walletType: string,
  fromChainId?: number,
  toChainId?: number,
  customTokenPairs?: Array<{ symbol: string; name: string; contractAddress?: string | null; network?: string | null }>
): boolean {
  // MM2 atomic swaps never require destination address (same wallet)
  // BUT: MM2 is currently paused, so we DO need destination addresses for cross-chain swaps
  // if (isAtomicSwapCompatible(fromToken) && isAtomicSwapCompatible(toToken)) {
  //   return false;
  // }
  
  // Determine if this is a cross-chain swap
  const isCrossChain = isCrossChainSwap(fromToken, toToken, fromChainId, toChainId, customTokenPairs);
  
  // Debug logging
  if (import.meta.env.DEV) {
    console.log('[destinationAddressUtils] requiresDestinationAddress:', {
      fromToken,
      toToken,
      walletType,
      fromChainId,
      toChainId,
      isCrossChain,
      fromPrimaryChain: fromChainId || getTokenPrimaryChain(fromToken, customTokenPairs),
      toPrimaryChain: toChainId || getTokenPrimaryChain(toToken, customTokenPairs)
    });
  }
  
  if (!isCrossChain) {
    // Same-chain swaps don't require destination address
    return false;
  }
  
  // For cross-chain swaps, determine chain types
  const fromPrimaryChain = fromChainId || getTokenPrimaryChain(fromToken, customTokenPairs);
  const toPrimaryChain = toChainId || getTokenPrimaryChain(toToken, customTokenPairs);
  
  if (!fromPrimaryChain || !toPrimaryChain) {
    // Can't determine - default to requiring address for safety
    return true;
  }
  
  // Get chain metadata to determine chain types
  const fromStringChainId = getStringChainIdFromNumeric(fromPrimaryChain);
  const toStringChainId = getStringChainIdFromNumeric(toPrimaryChain);
  
  if (!fromStringChainId || !toStringChainId) {
    // Can't determine - default to requiring address
    return true;
  }
  
  const fromMetadata = getChainMetadata(fromStringChainId);
  const toMetadata = getChainMetadata(toStringChainId);
  
  if (!fromMetadata || !toMetadata) {
    // Can't determine - default to requiring address
    return true;
  }
  
  // Cross-chain swaps between different chain types require destination address
  // Examples:
  // - UTXO → EVM: requires EVM address
  // - EVM → UTXO: requires UTXO address
  // - EVM → EVM: may require address if wallet doesn't support destination chain
  // - UTXO → UTXO: Different UTXO chains (e.g., RVN → BTC) require destination address
  
  // Check if this is a UTXO → UTXO cross-chain swap (different UTXO chains)
  const isUTXOSwap = isAtomicSwapCompatible(fromToken) && isAtomicSwapCompatible(toToken);
  if (isUTXOSwap && fromToken.toUpperCase() !== toToken.toUpperCase()) {
    // Different UTXO chains (e.g., RVN → BTC) require destination address
    // MM2 is paused, so we need destination addresses for these swaps
    return true;
  }
  
  // If wallet is UTXO-based (Ravencoin Core) and destination is EVM, require address
  if (walletType === 'ravencoin' && toMetadata.type === 'EVM') {
    return true;
  }
  
  // If wallet is EVM-based (Web3) and destination is UTXO, require address
  if (walletType === 'web3' && toMetadata.type === 'UTXO') {
    return true;
  }
  
  // If wallet is EVM-based and destination is different EVM chain, 
  // check if wallet supports the destination chain
  // For now, we'll require address for all cross-chain EVM swaps
  // (user might want to send to a different wallet on destination chain)
  if (walletType === 'web3' && fromMetadata.type === 'EVM' && toMetadata.type === 'EVM') {
    // Cross-chain EVM swaps may require destination address
    // This depends on the bridge/provider - some use wallet address, some allow custom
    // For safety, we'll require it unless explicitly handled by the provider
    return true;
  }
  
  // If wallet is EVM-based and destination is OTHER (Solana, ICP, etc.), require address
  if (walletType === 'web3' && toMetadata.type === 'OTHER') {
    return true;
  }
  
  // Default: require address for cross-chain swaps
  return true;
}

/**
 * Get the expected address format for the destination chain
 * @param toToken - Destination token symbol
 * @param toChainId - Optional destination chain ID (numeric)
 * @returns Format hint string
 */
export function getDestinationAddressFormat(
  toToken: string,
  toChainId?: number,
  customTokenPairs?: Array<{ symbol: string; name: string; contractAddress?: string | null; network?: string | null }>
): string {
  // Special case: Monero (XMR) - privacy coin with unique address format
  if (toToken.toUpperCase() === 'XMR') {
    return 'Monero address (starts with 4 or 8, 95 characters)';
  }
  
  // Special case: Bitcoin (BTC) - BTC chain ID maps to ravencoin, but we need BTC format
  if (toToken.toUpperCase() === 'BTC') {
    return 'Bitcoin address (starts with 1, 3, or bc1)';
  }
  
  // Special case: Litecoin (LTC)
  if (toToken.toUpperCase() === 'LTC') {
    return 'Litecoin address (starts with L, M, 3, or ltc1)';
  }
  
  // Special case: Dogecoin (DOGE)
  if (toToken.toUpperCase() === 'DOGE') {
    return 'Dogecoin address (starts with D)';
  }
  
  // Special case: Kaspa (KAS)
  if (toToken.toUpperCase() === 'KAS') {
    return 'Kaspa address (starts with kaspa:)';
  }
  
  // Special case: Ravencoin (RVN)
  if (toToken.toUpperCase() === 'RVN') {
    return 'Ravencoin address (starts with R)';
  }
  
  // Special case: Solana (SOL)
  if (toToken.toUpperCase() === 'SOL') {
    return 'Solana address (base58, 32-44 characters)';
  }
  
  // Special case: Zcash (ZEC)
  if (toToken.toUpperCase() === 'ZEC') {
    return 'Zcash address (starts with t1, t3, zs1, or z)';
  }
  
  const primaryChain = toChainId || getTokenPrimaryChain(toToken, customTokenPairs);
  
  if (!primaryChain) {
    return 'Enter destination address';
  }
  
  return getAddressFormatForChain(primaryChain);
}

/**
 * Get a user-friendly description of why destination address is required
 * @param fromToken - Source token symbol
 * @param toToken - Destination token symbol
 * @param walletType - Type of wallet
 * @param fromChainId - Optional source chain ID
 * @param toChainId - Optional destination chain ID
 * @returns Description string
 */
export function getDestinationAddressRequirementReason(
  fromToken: string,
  toToken: string,
  walletType: string,
  fromChainId?: number,
  toChainId?: number,
  customTokenPairs?: Array<{ symbol: string; name: string; contractAddress?: string | null; network?: string | null }>
): string {
  const isCrossChain = isCrossChainSwap(fromToken, toToken, fromChainId, toChainId, customTokenPairs);
  
  if (!isCrossChain) {
    return '';
  }
  
  const toPrimaryChain = toChainId || getTokenPrimaryChain(toToken, customTokenPairs);
  if (!toPrimaryChain) {
    return 'This is a cross-chain swap. Please enter the destination address.';
  }
  
  const toStringChainId = getStringChainIdFromNumeric(toPrimaryChain);
  if (!toStringChainId) {
    return 'This is a cross-chain swap. Please enter the destination address.';
  }
  
  const toMetadata = getChainMetadata(toStringChainId);
  if (!toMetadata) {
    return 'This is a cross-chain swap. Please enter the destination address.';
  }
  
  // Provide specific reason based on chain types
  if (walletType === 'ravencoin' && toMetadata.type === 'EVM') {
    return `This is a cross-chain swap from Ravencoin to ${toMetadata.displayName}. Please enter an EVM address to receive ${toToken}.`;
  }
  
  if (walletType === 'web3' && toMetadata.type === 'UTXO') {
    return `This is a cross-chain swap from EVM to ${toMetadata.displayName}. Please enter a ${toMetadata.displayName} address to receive ${toToken}.`;
  }
  
  if (walletType === 'web3' && toMetadata.type === 'EVM') {
    return `This is a cross-chain swap to ${toMetadata.displayName}. You may want to specify a different address on the destination chain.`;
  }
  
  return `This is a cross-chain swap to ${toMetadata.displayName}. Please enter the destination address.`;
}

